import React, { memo, useEffect, useMemo, useState, useContext, useRef, useCallback } from 'react';
import './incidents.css';
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  Backdrop,
  Checkbox,
  Chip,
  Autocomplete,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import IncidentsTable from '../../components/Incidents/IncidentsTable';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  getCallStatus,
  getDepartmentsList,
  getEmployeesList,
  getFormSettings,
  getIncidentInfo,
  getIncidentsList,
  syncNeris,
} from '../../store/incidentsReducer';
import useIncidentsColumns from './hooks/useIncidentsColumns';
import ViewIncidentModal from '../../components/Incidents/modals/ViewIncidentModal';
import EditIncidentModal from '../../components/Incidents/modals/EditIncidentModal';
import { debounce } from 'lodash';
import moment from 'moment';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
// import ImportXml from './ImportXml';
import { useTableSearch } from '../../hooks/tables/useTableSearch';
import {
  deleteFilterParamsFromUrl,
  getFilterParams,
} from '../../hooks/useFiltersUrlState';
import { useSearchParams } from 'react-router-dom';
import CreateNote from '../../components/Incidents/modals/CreateNote';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  initSettings,
  SCHEMA,
} from '../../components/Incidents/FilterIncidents/urlConfig';
import PropertyDamageModal from '../../components/Incidents/modals/PropertyDamageModal';
import IncidentPrintView from '../../components/Incidents/modals/IncidentPrintView';
import AuthContext from '../../context/AuthContext';
import {
  PeriodSelector,
  StartDatePicker,
  EndDatePicker,
  pickerValuesMap,
  pickerVariants,
  setDateRangeInUrl,
  useDateRangeFilter,
  generateYearOptions,
  pickerLabels,
} from '../../components/DatePickers/DateRangeFilter';

const datePickerPresetOptions = [
  { value: pickerValuesMap.custom,    label: pickerLabels[pickerValuesMap.custom] },
  { value: pickerValuesMap.last7Days, label: pickerLabels[pickerValuesMap.last7Days] },
  { value: pickerValuesMap.last30Days,label: pickerLabels[pickerValuesMap.last30Days] },
  { value: pickerValuesMap.lastYear,  label: pickerLabels[pickerValuesMap.lastYear] },
  { value: pickerValuesMap.today,     label: pickerLabels[pickerValuesMap.today] },
  { value: pickerValuesMap.mtd,       label: pickerLabels[pickerValuesMap.mtd] },
  { value: pickerValuesMap.ytd,       label: pickerLabels[pickerValuesMap.ytd] },
  ...generateYearOptions(2),
];

const INCIDENT_TYPES = ['Fire', 'Medical', 'Rescue', 'Hazsit', 'Noemerg', 'Lawenforce', 'Other'];
const LOCAL_NERIS_STATUSES = ['New', 'In Progress', 'Finished'];
const USFA_NERIS_STATUSES = ['Submitted', 'Rejected', 'Complete', 'In Progress', 'Failed', 'N/A'];
const RESPONSE_TIMES = ['<5 Min', '5-10 Min', '10+ Min'];

const selectSx = {
  height: '40px',
  fontSize: '14px',
  minWidth: '130px',
  backgroundColor: '#fff',
};

const Incidents = () => {
  const [searchParams, setParams] = useSearchParams();
  const {
    searchQuery,
    paginationModel,
    setSearchQuery,
    setPaginationModel,
    localSearchState,
    setLocalSearchState,
  } = useTableSearch();

  const [viewIncident, setViewIncident] = useState(null);
  const [editIncident, setEditIncident] = useState(null);
  const [openCreateNoteModal, setOpenCreateNoteModal] = useState(false);
  const [openPropertyDamageModal, setOpenPropertyDamageModal] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const { department } = useContext(AuthContext);

  // Inline filter state (URL-driven via searchParams)
  const getParam = (key) => searchParams.get(key) || '';

  const dateRangeFilter = useDateRangeFilter({
    defaultValue: pickerValuesMap.last30Days,
  });

  let {
    incidentsList,
    loading,
    incidentsPagination,
    employeesList,
    callStatus,
    selectedIncidentInfo,
  } = useSelector((state) => state.incidentsReducer);

  const { permissions } = useSelector(
    (state) => state.nerisSettingsAuthoritiesReducer
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDepartmentsList());
    dispatch(getEmployeesList());
    dispatch(getCallStatus());
  }, [dispatch]);

  const requestForIncidents = () => {
    const params = getFilterParams(SCHEMA, initSettings);
    dispatch(
      getIncidentsList({
        page: paginationModel.page + 1,
        searchQuery: searchQuery,
        pageSize: paginationModel.pageSize,
        ...params,
      })
    );
  };

  useEffect(() => {
    requestForIncidents();
  }, [paginationModel.page, paginationModel.pageSize, searchQuery]);

  const debouncedHandlerSearch = useMemo(
    () =>
      debounce((query) => {
        setParams((prev) => {
          deleteFilterParamsFromUrl(prev, SCHEMA);
          return prev;
        });
        setSearchQuery(query);
      }, 1000),
    [paginationModel.pageSize]
  );

  const handleSearchChange = (e) => {
    setLocalSearchState(e.target.value);
    debouncedHandlerSearch(e.target.value);
  };

  const clearSearch = () => {
    setLocalSearchState('');
    debouncedHandlerSearch('');
  };

  const handleSelectFilter = (urlKey) => (e) => {
    const value = e.target.value;
    setParams((prev) => {
      if (value) {
        prev.set(urlKey, value);
      } else {
        prev.delete(urlKey);
      }
      prev.set('page', '0');
      return prev;
    });
  };

  const openIncidentInfo = (id, edit = false) => {
    if (edit) {
      dispatch(getFormSettings(id));
      setEditIncident(true);
    } else {
      setViewIncident(true);
    }
    dispatch(getIncidentInfo(id));
  };

  const createIncident = () => {
    dispatch(getFormSettings());
    setEditIncident(true);
  };

  const handlerSyncNeris = (id) => {
    dispatch(syncNeris(id)).then((resp) => {
      if (resp) {
        requestForIncidents();
      }
    });
  };

  const printReadyRef = useRef(null);

  const handlePrintReady = useCallback(() => {
    if (printReadyRef.current) {
      printReadyRef.current();
      printReadyRef.current = null;
    }
  }, []);

  const handlePrintIncident = (id) => {
    setPrintLoading(true);
    dispatch(getIncidentInfo(id)).then(() => {
      // Wait for IncidentPrintView's secondary fetches to finish
      const waitForReady = new Promise((resolve) => {
        printReadyRef.current = resolve;
        // Safety timeout — print anyway after 5s
        setTimeout(resolve, 5000);
      });
      waitForReady.then(() => {
        setPrintLoading(false);
        window.print();
      });
    });
  };

  const columns = useIncidentsColumns({
    openIncidentInfo,
    handlerSyncNeris,
    setOpenCreateNoteModal,
		handlePrintIncident,
    permissions,
    employeesList,
    requestForIncidents,
    callStatus,
    setOpenPropertyDamageModal,
  });

  const handleApplyFilter = () => {
    requestForIncidents();
  };

  const filterMyReports = () => {
    setParams((prev) => {
      prev.set('employee', permissions.id);
      return prev;
    });
    // Use setTimeout so URL params are committed before the API call
    setTimeout(() => requestForIncidents(), 0);
  };

  const isMyReports = !!getParam('employee');

  const clearMyReports = () => {
    setParams((prev) => {
      prev.delete('employee');
      return prev;
    });
    setTimeout(() => requestForIncidents(), 0);
  };
  
  const rows = useMemo(
    () =>
      incidentsList.map((i) => ({
        ...i,
        id: i.incident_id,
        neris_status: i.neris_status ? i.neris_status : 'N/A',
      })),
    [incidentsList]
  );

  const onPaginationModelChange = (e) => {
    setPaginationModel(e);
  };

  // Build call status options for Status dropdown
  const callStatusOptions = useMemo(
    () => callStatus?.map((item) => ({ value: item.label, label: item.label })) || [],
    [callStatus]
  );

  return (
    <>
      <Box>
        <Card
          className="p-3 mt-4"
          sx={{ boxShadow: '0px 4px 4px 0px #0000000F' }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Incidents
            </Typography>
            <Box className="d-flex" sx={{ gap: 1.5, alignItems: 'center' }}>
              {/* Search */}
              <TextField
                color="secondary"
                placeholder="Search incidents..."
                size="small"
                value={localSearchState}
                onChange={handleSearchChange}
                sx={{ width: '240px', backgroundColor: '#fff' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchQuery.length > 0 ? (
                        <ClearIcon onClick={clearSearch} sx={{ cursor: 'pointer' }} />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilter}
                sx={{
                  backgroundColor: '#BF1D2D',
                  '&:hover': { backgroundColor: '#a01825' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Apply Filter
              </Button>
              {isMyReports ? (
                <Button
                  variant="outlined"
                  onClick={clearMyReports}
                  sx={{
                    borderColor: '#BF1D2D',
                    color: '#BF1D2D',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#a01825', backgroundColor: '#fef2f2' },
                  }}
                >
                  All Reports
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={filterMyReports}
                  sx={{
                    borderColor: '#BF1D2D',
                    color: '#BF1D2D',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#a01825', backgroundColor: '#fef2f2' },
                  }}
                >
                  My Reports
                </Button>
              )}
            </Box>
          </Box>

          {/* Filter row */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              mb: 2,
            }}
          >
            {/* Start date */}
            <Box sx={{ minWidth: '140px', flex: '1 1 140px' }}>
              <StartDatePicker
                fromDate={dateRangeFilter.fromDate}
                handleChangeFromDate={dateRangeFilter.handleChangeFromDate}
                toDate={dateRangeFilter.toDate}
                isCustomDateRange={true}
              />
            </Box>

            {/* End date */}
            <Box sx={{ minWidth: '140px', flex: '1 1 140px' }}>
              <EndDatePicker
                toDate={dateRangeFilter.toDate}
                handleChangeToDate={dateRangeFilter.handleChangeToDate}
                fromDate={dateRangeFilter.fromDate}
                isCustomDateRange={true}
              />
            </Box>

            {/* Incident Type */}
            <FormControl size="small" sx={{ minWidth: '120px', flex: '1 1 120px' }}>
              <Select
                displayEmpty
                value={getParam('type')}
                onChange={handleSelectFilter('type')}
                sx={selectSx}
                renderValue={(v) => v || <span style={{ color: '#999' }}>Type</span>}
              >
                <MenuItem value="">All Types</MenuItem>
                {INCIDENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Status */}
            <FormControl size="small" sx={{ minWidth: '120px', flex: '1 1 120px' }}>
              <Select
                displayEmpty
                value={getParam('status')}
                onChange={handleSelectFilter('status')}
                sx={selectSx}
                renderValue={(v) => v || <span style={{ color: '#999' }}>Call Status</span>}
              >
                <MenuItem value="">All</MenuItem>
                {callStatusOptions.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Local NERIS Status */}
            <FormControl size="small" sx={{ minWidth: '140px', flex: '1 1 140px' }}>
              <Select
                displayEmpty
                value={getParam('form_status')}
                onChange={handleSelectFilter('form_status')}
                sx={selectSx}
                renderValue={(v) => v || <span style={{ color: '#999' }}>Local</span>}
              >
                <MenuItem value="">All Local</MenuItem>
                {LOCAL_NERIS_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            {/* USFA NERIS Status */}
            <FormControl size="small" sx={{ minWidth: '140px', flex: '1 1 140px' }}>
              <Select
                displayEmpty
                value={getParam('nerisStatus')}
                onChange={handleSelectFilter('nerisStatus')}
                sx={selectSx}
                renderValue={(v) => v || <span style={{ color: '#999' }}>USFA</span>}
              >
                <MenuItem value="">All USFA</MenuItem>
                {USFA_NERIS_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Employee multi-select with search */}
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              limitTags={1}
              getLimitTagsText={(more) => `+${more}`}
              options={employeesList || []}
              getOptionLabel={(option) => option.label || ''}
              value={(employeesList || []).filter((emp) =>
                (getParam('employee') ? getParam('employee').split(',') : []).includes(String(emp.id))
              )}
              onChange={(_, newValue) => {
                setParams((prev) => {
                  if (newValue.length > 0) {
                    prev.set('employee', newValue.map((v) => String(v.id)).join(','));
                  } else {
                    prev.delete('employee');
                  }
                  prev.set('page', '0');
                  return prev;
                });
              }}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    (getParam('employee') ? getParam('employee').split(',') : []).length === 0
                      ? 'Employee'
                      : ''
                  }
                />
              )}
              sx={{
                minWidth: '220px',
                flex: '1 1 220px',
                '& .MuiOutlinedInput-root': {
                  ...selectSx,
                  height: '40px',
                  flexWrap: 'nowrap',
                  paddingRight: '65px !important',
                },
              }}
              ListboxProps={{ style: { maxHeight: 350 } }}
            />
          </Box>

          {/* Table */}
          <Box>
            {loading || !employeesList ? (
              <Box
                sx={{ minHeight: '100px' }}
                className="w-100 h-100 d-flex justify-content-center align-items-center"
              >
                <CircularProgress />
              </Box>
            ) : !loading && incidentsList.length === 0 ? (
              <Typography className="mt-2" color="#6E7C87">
                No incidents found matching your search.
              </Typography>
            ) : (
              <IncidentsTable
                rows={rows}
                columns={columns}
                onPaginationModelChange={onPaginationModelChange}
                paginationModel={paginationModel}
                rowsCount={incidentsPagination?.total ?? 0}
              />
            )}
          </Box>
        </Card>
      </Box>

      {/* ── Print loading backdrop ── */}
      <Backdrop
        open={printLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: '#fff',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <PrintIcon sx={{ fontSize: 48, opacity: 0.9 }} />
        <CircularProgress color="inherit" />
        <Typography
          sx={{ mt: 1, fontFamily: 'karla, sans-serif', fontSize: '1rem' }}
        >
          Loading report…
        </Typography>
      </Backdrop>

      {/* ── Print portal ── */}
      <IncidentPrintView incident={selectedIncidentInfo} department={department} onReady={handlePrintReady} />

      <ViewIncidentModal
        viewIncident={viewIncident}
        setViewIncident={setViewIncident}
      />
      {!!editIncident && (
        <EditIncidentModal
          editIncident={editIncident}
          setEditIncident={setEditIncident}
          onRevalidateData={requestForIncidents}
        />
      )}
      <CreateNote open={openCreateNoteModal} setOpen={setOpenCreateNoteModal} />
      <PropertyDamageModal
        open={openPropertyDamageModal}
        setOpen={setOpenPropertyDamageModal}
      />
    </>
  );
};

export default memo(Incidents);
